import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { taskAPI, dailyStandupAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
    cn,
    formatDate,
    priorityConfig,
    statusColors,
} from '../lib/utils';
import {
    CheckCircle2,
    Clock,
    Calendar,
    Loader2,
    ClipboardList,
    ArrowLeft,
    Plus,
    Search,
    X,
} from 'lucide-react';
import { toast } from 'sonner';

export default function StandupPlanPage() {
    const navigate = useNavigate();
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();

    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState(null);
    const [candidateTasks, setCandidateTasks] = useState([]);
    const [showPicker, setShowPicker] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [planning, setPlanning] = useState(false);

    const todayStr = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Jakarta' }).format(new Date());

    const fetchData = useCallback(async () => {
        if (!currentWorkspace?.id || !user?.id) return;
        if (!currentWorkspace.daily_standup_enabled) return;
        setLoading(true);
        try {
            // Get my summary (3 categories)
            const summaryRes = await dailyStandupAPI.getMySummary(currentWorkspace.id);
            setSummary(summaryRes.data);

            // Get my tasks that are not Done and not already planned today
            const plannedIds = new Set((summaryRes.data?.planned_today || []).map(t => t.id));
            const tasksRes = await taskAPI.getByWorkspace(currentWorkspace.id, {
                assignee_id: user.id,
                exclude_done: true,
            });
            const allMyTasks = tasksRes.data || [];
            // Candidate picker: only Not Started tasks (Active are auto-planned, Done/Review excluded)
            setCandidateTasks(allMyTasks.filter(t =>
                !plannedIds.has(t.id)
                && t.category !== 'DAILY_TASK'
                && (t.status_group === 'Not Started' || !t.status_group)
            ));
        } catch (err) {
            console.error('Error fetching plan data:', err);
            toast.error('Gagal memuat data');
        } finally {
            setLoading(false);
        }
    }, [currentWorkspace?.id, currentWorkspace?.daily_standup_enabled, user?.id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const plannedIds = new Set((summary?.planned_today || []).map(t => t.id));

    const filteredCandidates = candidateTasks.filter(t => {
        if (plannedIds.has(t.id)) return false;
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return t.title.toLowerCase().includes(term)
                || (t.task_code && t.task_code.toLowerCase().includes(term));
        }
        return true;
    });

    const handleAddPlan = async (task) => {
        setPlanning(true);
        try {
            // Set standup_plan_date to today — does NOT modify task's due_date
            await taskAPI.update(task.id, { standup_plan_date: todayStr });
            toast.success(`"${task.title}" dijadwalkan untuk hari ini`);
            // Refresh
            const summaryRes = await dailyStandupAPI.getMySummary(currentWorkspace.id);
            setSummary(summaryRes.data);
            const plannedIds = new Set((summaryRes.data?.planned_today || []).map(t => t.id));
            const tasksRes = await taskAPI.getByWorkspace(currentWorkspace.id, {
                assignee_id: user.id,
                exclude_done: true,
            });
            setCandidateTasks((tasksRes.data || []).filter(t => !plannedIds.has(t.id)));
        } catch (err) {
            console.error('Error scheduling task:', err);
            toast.error('Gagal menjadwalkan task');
        } finally {
            setPlanning(false);
        }
    };

    const handleRemovePlan = async (task) => {
        setPlanning(true);
        try {
            // Clear standup_plan_date — does NOT modify task's due_date
            await taskAPI.update(task.id, { standup_plan_date: "" });
            toast.success(`"${task.title}" dihapus dari rencana hari ini`);
            const summaryRes = await dailyStandupAPI.getMySummary(currentWorkspace.id);
            setSummary(summaryRes.data);
            const plannedIds = new Set((summaryRes.data?.planned_today || []).map(t => t.id));
            const tasksRes = await taskAPI.getByWorkspace(currentWorkspace.id, {
                assignee_id: user.id,
                exclude_done: true,
            });
            setCandidateTasks((tasksRes.data || []).filter(t => !plannedIds.has(t.id)));
        } catch (err) {
            console.error('Error removing plan:', err);
            toast.error('Gagal menghapus rencana');
        } finally {
            setPlanning(false);
        }
    };

    const renderTaskItem = (task, { showRemove = false, showAdd = false } = {}) => {
        const priority = priorityConfig[task.priority] || priorityConfig.none;
        const statusColor = statusColors[task.status] || '#9FB0AC';

        return (
            <div
                key={task.id}
                className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                onClick={() => navigate(`/tasks?task=${task.id}`)}
            >
                <div
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: statusColor }}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        {task.task_code && (
                            <span className="text-xs font-mono text-gray-400 flex-shrink-0">
                                {task.task_code}
                            </span>
                        )}
                        <span className="text-sm font-medium truncate text-gray-700 dark:text-gray-200">
                            {task.title}
                        </span>
                    </div>
                </div>
                <span
                    className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: `${priority.color}20`, color: priority.color }}
                >
                    {priority.label}
                </span>
                {showRemove && (
                    <button
                        onClick={(e) => { e.stopPropagation(); handleRemovePlan(task); }}
                        disabled={planning}
                        className="p-1 rounded text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                        title="Hapus dari rencana"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
                {showAdd && (
                    <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); handleAddPlan(task); }}
                        disabled={planning}
                    >
                        <Plus className="w-3 h-3 mr-1" />
                        Pilih
                    </Button>
                )}
            </div>
        );
    };

    const renderSection = (Icon, title, tasks, color, emptyMessage, renderItemProps = {}) => {
        return (
            <Card>
                <CardContent className="pt-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Icon className="w-4 h-4" style={{ color }} />
                        <span className="text-sm font-semibold" style={{ color }}>
                            {title}
                        </span>
                        <span className="text-xs text-gray-400">({tasks.length})</span>
                    </div>
                    {tasks.length === 0 ? (
                        <p className="text-xs text-gray-400 italic py-2 px-3">
                            {emptyMessage}
                        </p>
                    ) : (
                        <div className="space-y-1.5">
                            {tasks.map(t => renderTaskItem(t, renderItemProps))}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    };

    if (!currentWorkspace) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <p className="text-gray-400">Pilih workspace terlebih dahulu</p>
                </div>
            </Layout>
        );
    }

    if (!currentWorkspace.daily_standup_enabled) {
        return (
            <Layout>
                <div className="flex flex-col items-center justify-center h-[60vh] text-center px-4">
                    <ClipboardList className="w-12 h-12 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">
                        Daily Standup dinonaktifkan untuk workspace ini
                    </p>
                    <p className="text-sm text-gray-400 mt-1">
                        Owner atau admin dapat mengaktifkannya di Settings → Workspace.
                    </p>
                </div>
            </Layout>
        );
    }

    const plannedToday = summary?.planned_today || [];

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={() => navigate('/daily-standup')}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                            Buat Rencana Hari Ini
                        </h1>
                        <p className="text-sm text-gray-400">
                            {currentWorkspace.name} — {summary?.date || formatDate(todayStr)}
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-accent" />
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Section 1: Done Yesterday (Review) */}
                        {renderSection(
                            CheckCircle2,
                            'Task yang diselesaikan kemarin (Review)',
                            summary?.done_yesterday || [],
                            '#F5C26B',
                            'Tidak ada task yang berpindah ke Review kemarin'
                        )}

                        {/* Section 2: In Progress */}
                        {renderSection(
                            Clock,
                            'Task yang sedang dikerjakan',
                            summary?.in_progress || [],
                            '#6EC1E4',
                            'Tidak ada task yang sedang dikerjakan'
                        )}

                        {/* Section 3: Planned Today — with picker */}
                        <Card className="border-brand-accent/30">
                            <CardContent className="pt-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-green-500" />
                                        <span className="text-sm font-semibold text-green-700 dark:text-green-400">
                                            Rencana hari ini
                                        </span>
                                        <span className="text-xs text-gray-400">({plannedToday.length})</span>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="h-7 text-xs gap-1"
                                        onClick={() => setShowPicker(!showPicker)}
                                    >
                                        <Plus className="w-3 h-3" />
                                        Pilih Task
                                    </Button>
                                </div>

                                {/* Planned tasks list */}
                                {plannedToday.length === 0 ? (
                                    <p className="text-xs text-gray-400 italic py-2 px-3">
                                        Belum ada rencana untuk hari ini. Klik "Pilih Task" untuk menambahkan.
                                    </p>
                                ) : (
                                    <div className="space-y-1.5 mb-3">
                                        {plannedToday.map(t => renderTaskItem(t, { showRemove: true }))}
                                    </div>
                                )}

                                {/* Task picker */}
                                {showPicker && (
                                    <div className="border-t border-gray-100 dark:border-gray-800 pt-3 mt-2">
                                        <div className="relative mb-2">
                                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                                            <Input
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                placeholder="Cari task..."
                                                className="h-8 pl-8 text-xs"
                                            />
                                        </div>
                                        {filteredCandidates.length === 0 ? (
                                            <p className="text-xs text-gray-400 italic py-2 px-3">
                                                {searchTerm
                                                    ? 'Tidak ada task yang cocok'
                                                    : 'Tidak ada task tersedia. Semua task sudah dijadwalkan atau sudah Done.'}
                                            </p>
                                        ) : (
                                            <div className="space-y-1.5 max-h-60 overflow-y-auto">
                                                {filteredCandidates.map(t =>
                                                    renderTaskItem(t, { showAdd: true })
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </Layout>
    );
}